import React from 'react';
import { useDropzone } from 'react-dropzone';
import { ModalMenuUI } from '../../../Modal/ModalMenuUI';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import Surface from '../../../Surface/Surface';
import { ImageCropper } from '../UserAvatar/ImageCropper';
import { CircularProgress } from '../../../Progress/CircularProgress';
import { useClassName } from '../../../utils/useClassName';
import { isMobile } from 'react-device-detect';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { ModalNoticeUI } from '../../../Modal/ModalNoticeUI';

type PreviewPicture = { name: string; src: any };

interface ModalProps {
  loading?: boolean;
  onCloseEvent?: () => void;
  onClickSave?: (image) => void;
}

interface ImageCropperProps {
  image: PreviewPicture;
  shouldCrop: boolean;
  disabled?: boolean;
  shape?: 'circle' | 'square';
  setAvatar: (img) => void;
}

interface DropzoneProps extends ImageCropperProps {
  onDrop?: (pic) => void;
  readingFile?: boolean;
}

type Props = ModalProps &
  DropzoneProps &
  ImageCropperProps & {
    title: string;
  };

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    dropArea: {
      minHeight: 300,
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: '1fr',
      alignItems: 'stretch',
      justifyItems: 'stretch'
    },
    dropActive: {
      border: `1px ${theme.customPalette.secondary.light} solid`,
      backgroundColor: theme.customPalette.surfaceState.getSurfaceStateColor(
        'transparent',
        'secondary',
        ['dragOver']
      )
    },
    dropRejected: {
      border: `1px ${theme.customPalette.dangerError} solid`
    },
    textContainer: {
      margin: 'auto',
      paddingBottom: 10
    },
    mainText: {
      display: 'flex',
      alignItems: 'center'
    },
    spinner: {
      position: 'absolute',
      top: '60%',
      left: '50%'
    }
  })
);

const ProfilePictureDropzone: React.FC<DropzoneProps> = (props) => {
  const { image, disabled, shouldCrop, shape, onDrop, setAvatar, readingFile } =
    props;
  const classes = useStyles();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: { 'image/*': [] },
    useFsAccessApi: false //doesn't work with playwright testing
  });

  return (
    <Surface
      border={isDragActive}
      background="transparent"
      cornerRadius="mediumEmphasis"
      corners="rounded"
      className={useClassName(
        classes.root,
        isDragActive && classes.dropActive,
        isDragReject && classes.dropRejected
      )}
    >
      <div
        {...getRootProps({
          className: classes.dropArea
        })}
      >
        <input {...getInputProps()} />

        <div className={classes.textContainer}>
          <Typography variant={isMobile ? SizeVariants.lg : SizeVariants.md}>
            {isDragAccept ? (
              <>Drop the picture here</>
            ) : isDragReject ? (
              <>Only pictures will be accepted</>
            ) : (
              <div className={classes.mainText}>
                <span>{isMobile ? '' : 'Drag picture here or '}</span>
                <div style={{ margin: '-1.5px 4px 0 4px' }}>
                  <Button
                    background="primary"
                    buttonStyle="textButton"
                    label={isMobile ? 'Select a picture' : 'select a picture'}
                    onClick={open}
                    size={isMobile ? SizeVariants.lg : SizeVariants.md}
                  />
                </div>
                <span> from your device</span>
              </div>
            )}
          </Typography>
        </div>

        {readingFile && <CircularProgress className={classes.spinner} />}
        {!readingFile && image && (
          <ImageCropper
            shape={shape}
            image={image}
            width={isMobile ? undefined : 500}
            height={250}
            scale={1}
            radius={150}
            minRadius={50}
            setAvatar={setAvatar}
            shouldCrop={shouldCrop}
            disabled={disabled}
          />
        )}
      </div>
    </Surface>
  );
};

export const ChangeProfilePictureModalUI: React.FunctionComponent<Props> = (
  props
) => {
  const {
    onCloseEvent,
    onDrop,
    image,
    onClickSave,
    setAvatar,
    shouldCrop,
    loading,
    readingFile,
    title,
    shape
  } = props;

  return (
    <>
      {isMobile ? (
        <ModalNoticeUI
          title={title}
          loading={loading}
          onCloseEvent={onCloseEvent}
          footerRightComponent={
            <>
              <Button
                label="Cancel"
                background="secondary"
                onClick={onCloseEvent}
                size={SizeVariants.lg}
              />
              <Button
                label="Save"
                disabled={!image}
                background="primary"
                onClick={onClickSave}
                size={SizeVariants.lg}
              />
            </>
          }
        >
          <ProfilePictureDropzone
            shape={shape}
            onDrop={onDrop}
            image={image}
            setAvatar={setAvatar}
            shouldCrop={shouldCrop}
            disabled={loading} // disable the cropper while processing the new avatar
            readingFile={readingFile}
          />
        </ModalNoticeUI>
      ) : (
        <ModalMenuUI
          title={title}
          loading={loading}
          onCloseEvent={onCloseEvent}
          footerRightComponent={
            <>
              <Button
                label="Cancel"
                background="secondary"
                onClick={onCloseEvent}
              />
              <Button
                label="Save"
                disabled={!image}
                background="primary"
                onClick={onClickSave}
              />
            </>
          }
        >
          <ProfilePictureDropzone
            shape={shape}
            onDrop={onDrop}
            image={image}
            setAvatar={setAvatar}
            shouldCrop={shouldCrop}
            disabled={loading} // disable the cropper while processing the new avatar
            readingFile={readingFile}
          />
        </ModalMenuUI>
      )}
    </>
  );
};
